import { useEffect, useRef, useState } from 'react';
import Pusher from 'pusher-js';
import { backendHost, wsHost } from 'core/api';
import { PUSHER_APP_KEY } from '../constants/pusher';
import { userStorage } from '../utils/userStorage';
import { tokenStorage } from '../utils/tokenStorage';

interface UseWebSocketOptions {
  onEvent?: (event: string, data: unknown) => void;
  enabled?: boolean;
}

export const useWebSocket = (options: UseWebSocketOptions = {}) => {
  const { onEvent, enabled = true } = options;
  const pusherRef = useRef<Pusher | null>(null);
  const channelRef = useRef<ReturnType<Pusher['subscribe']> | null>(null);
  const onEventRef = useRef(onEvent);
  const [isConnected, setIsConnected] = useState(false);
  const [socketId, setSocketId] = useState<string | null>(null);

  // Обновляем ref при изменении onEvent, чтобы не пересоздавать подключение
  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  useEffect(() => {
    if (!enabled) {
      // Отключаем соединение, если disabled
      if (pusherRef.current) {
        if (channelRef.current) {
          pusherRef.current.unsubscribe(channelRef.current.name);
          channelRef.current = null;
        }
        pusherRef.current.disconnect();
        pusherRef.current = null;
        setIsConnected(false);
        setSocketId(null);
      }
      return;
    }

    // Если Pusher уже подключен, не создаем новое соединение
    if (pusherRef.current && pusherRef.current.connection.state === 'connected') {
      return;
    }

    const userId = userStorage.get();
    if (!userId) {
      console.warn('User ID not found in localStorage, skipping WebSocket connection');
      return;
    }

    // Извлекаем host из backendHost (убираем http:// или https://)
    const apiHost = wsHost?.replace(/^https?:\/\//, '') || '';
    const channelName = `private-user-${userId}`;

    // Инициализируем Pusher с кастомным хостом
    const pusher = new Pusher(PUSHER_APP_KEY, {
      cluster: '',
      wsHost: apiHost,
      httpHost: apiHost,
      wsPath: process.env.REACT_APP_WS_PATH,
      wsPort: 80,
      wssPort: 443,
      enabledTransports: ['ws', 'wss'],
      forceTLS: backendHost?.startsWith('https'),
      // Используем authEndpoint для авторизации на приватных каналах
      authEndpoint: `${backendHost}/broadcasting/auth`,
      auth: {
        headers: {
          Authorization: `Bearer ${tokenStorage.get()}`,
        },
      },
    });

    pusherRef.current = pusher;

    // Сохраняем ссылки на обработчики для корректной очистки
    const connectedHandler = () => {
      const currentSocketId = pusher.connection.socket_id;
      setSocketId(currentSocketId);
      setIsConnected(true);
    };

    const disconnectedHandler = () => {
      setIsConnected(false);
      setSocketId(null);
    };

    const errorHandler = (error: unknown) => {
      console.error('WebSocket error:', error);
    };

    // Обработчик состояния соединения
    const stateChangeHandler = (states: { previous: string; current: string }) => {
      if (states.current === 'connected' && !channelRef.current) {
        // После успешного подключения подписываемся на канал
        const channel = pusher.subscribe(channelName);
        channelRef.current = channel;

        channel.bind('pusher:subscription_error', (error: unknown) => {
          console.error('Subscription error:', error);
        });

        // Подписываемся на все события канала
        channel.bind_global((eventName: string, data: unknown) => {
          if (onEventRef.current) {
            onEventRef.current(eventName, data);
          }
        });
      }
    };

    // Привязываем обработчики
    pusher.connection.bind('connected', connectedHandler);
    pusher.connection.bind('disconnected', disconnectedHandler);
    pusher.connection.bind('error', errorHandler);
    pusher.connection.bind('state_change', stateChangeHandler);

    return () => {
      const currentPusher = pusherRef.current;
      if (!currentPusher) {
        return;
      }

      // Отписываемся от событий (unbind без функции отвяжет все обработчики)
      currentPusher.connection.unbind('connected');
      currentPusher.connection.unbind('disconnected');
      currentPusher.connection.unbind('error');
      currentPusher.connection.unbind('state_change');

      if (channelRef.current) {
        currentPusher.unsubscribe(channelRef.current.name);
        channelRef.current = null;
      }

      currentPusher.disconnect();
      pusherRef.current = null;
      setIsConnected(false);
      setSocketId(null);
    };
  }, [enabled]);

  return {
    isConnected,
    socketId,
    pusher: pusherRef.current,
    channel: channelRef.current,
  };
};
